import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Loader2, Filter as FilterIcon, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { TransactionRow } from '@/components/transactions/TransactionRow';
import { TransactionFormFields, TXN_FORM_CATEGORIES, emptyTxnForm, txnFormToApi, apiToTxnForm } from '@/components/transactions/TransactionFormFields';
import { Transactions as TxnsApi, Cards as CardsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR } from '@/lib/format';
import { toast } from 'sonner';

export default function Transactions() {
  const { user, demoLogin } = useAuth();
  const [txns, setTxns] = useState([]);
  const [cards, setCards] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [cardFilter, setCardFilter] = useState('all');
  // Phase 18 (issue #3) — additional filter dimensions. `monthFilter` is a
  // shortcut that overrides date_from/date_to when set; clearing it falls
  // back to whatever the explicit range fields say.
  const [monthFilter, setMonthFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyTxnForm());
  const [saving, setSaving] = useState(false);

  // Edit state ----------------------------------------------------------------
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(emptyTxnForm());
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (!user) await demoLogin();
      const [t, c] = await Promise.all([
        TxnsApi.list({ limit: 500 }),
        CardsApi.mine().catch(() => CardsApi.library()),
      ]);
      setTxns(t || []);
      setCards(c || []);
      if (!form.card_id && c && c.length) setForm((f) => ({ ...f, card_id: c[0].id }));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user?.id]);

  // Refresh on rb:data-changed (cards added/removed or txns mutated elsewhere)
  useEffect(() => {
    const handler = (e) => {
      const ent = e?.detail?.entity;
      if (ent === 'card' || ent === 'transaction') load();
    };
    window.addEventListener('rb:data-changed', handler);
    return () => window.removeEventListener('rb:data-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usedCategories = useMemo(
    () => Array.from(new Set(txns.map((t) => t.category).filter(Boolean))),
    [txns],
  );
  const allCategoriesForCombo = useMemo(
    () => Array.from(new Set([...TXN_FORM_CATEGORIES, ...usedCategories])),
    [usedCategories],
  );
  // Phase 18 — months present in the data, descending. Used for the
  // "By month" quick-pick dropdown.
  const monthsAvailable = useMemo(() => {
    const set = new Set();
    for (const t of txns) {
      const d = (t.date || '').slice(0, 7);
      if (d.length === 7) set.add(d);
    }
    return Array.from(set).sort().reverse();
  }, [txns]);

  // Active-filter count powers the "Filters · 3" badge on the trigger.
  const activeFilterCount =
    (category !== 'all' ? 1 : 0)
    + (cardFilter !== 'all' ? 1 : 0)
    + (monthFilter !== 'all' ? 1 : 0)
    + (dateFrom ? 1 : 0)
    + (dateTo ? 1 : 0);

  const clearAllFilters = () => {
    setQuery('');
    setCategory('all');
    setCardFilter('all');
    setMonthFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const filtered = txns.filter((t) => {
    const q = !query || (t.merchant || '').toLowerCase().includes(query.toLowerCase());
    const c = category === 'all' || t.category === category;
    const k = cardFilter === 'all' || t.card_id === cardFilter;
    const monthStr = (t.date || '').slice(0, 7);
    const m = monthFilter === 'all' || monthStr === monthFilter;
    // Range comparisons use the YYYY-MM-DD prefix so they work regardless of
    // whether the stored value is a date-only string or full ISO datetime.
    const dPrefix = (t.date || '').slice(0, 10);
    const df = !dateFrom || dPrefix >= dateFrom;
    const dt = !dateTo || dPrefix <= dateTo;
    return q && c && k && m && df && dt;
  });
  const total = filtered.reduce((s, t) => s + (t.amount || 0), 0);
  const rewards = filtered.reduce((s, t) => s + (t.reward_value_inr || 0), 0);

  const submitTxn = async () => {
    if (!form.merchant || !form.amount || !form.card_id) {
      toast.error('Merchant, amount, and card are required.');
      return;
    }
    setSaving(true);
    try {
      const dateIso = form.date ? new Date(`${form.date}T12:00:00Z`).toISOString() : undefined;
      // Phase 20 — decompose UI Mode into the storage triple.
      const modeFields = txnFormToApi(form);
      await TxnsApi.create({
        merchant: form.merchant,
        amount: Number(form.amount),
        category: form.category,
        mcc: form.mcc || undefined,
        ...modeFields,
        card_id: form.card_id,
        date: dateIso,
      });
      toast.success('Transaction logged');
      setOpen(false);
      setForm(emptyTxnForm(cards[0]?.id || ''));
      load();
    } catch (e) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // ----- Edit handlers -----
  const openEdit = (tx) => {
    setEditTarget(tx);
    setEditForm(apiToTxnForm(tx, cards[0]?.id || ''));
  };
  const closeEdit = () => {
    setEditTarget(null);
    setEditSaving(false);
  };
  const submitEdit = async () => {
    if (!editTarget) return;
    if (!editForm.merchant || !editForm.amount || !editForm.card_id) {
      toast.error('Merchant, amount, and card are required.');
      return;
    }
    setEditSaving(true);
    try {
      const dateIso = editForm.date
        ? new Date(`${editForm.date}T12:00:00Z`).toISOString()
        : undefined;
      const modeFields = txnFormToApi(editForm);
      await TxnsApi.update(editTarget.id, {
        merchant: editForm.merchant,
        amount: Number(editForm.amount),
        category: editForm.category,
        mcc: editForm.mcc || undefined,
        ...modeFields,
        card_id: editForm.card_id,
        date: dateIso,
      });
      toast.success('Transaction updated');
      closeEdit();
      load();
    } catch (e) {
      toast.error(e.message || 'Could not update');
      setEditSaving(false);
    }
  };

  if (loading) return <div className="min-h-[40vh] grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start sm:items-end justify-between flex-col sm:flex-row gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">History</div>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Transactions</h1>
          <p className="mt-1 text-sm text-muted-foreground">{filtered.length} entries · {formatINR(total, { compact: true })} spent · {formatINR(rewards, { compact: true })} earned</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="transactions-add-button"><Plus className="h-4 w-4" /> Log transaction</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Log a new transaction</DialogTitle></DialogHeader>
            <TransactionFormFields
              form={form}
              setForm={setForm}
              cards={cards}
              suggestions={allCategoriesForCombo}
              testidPrefix="transactions"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={submitTxn} disabled={saving} className="gap-1.5" data-testid="transactions-form-save">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row items-stretch gap-3" data-testid="transactions-filter-bar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by merchant..."
            className="pl-10 h-10 border-transparent bg-[hsl(var(--surface-2))]"
            data-testid="transactions-filter-search"
          />
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-10 w-full sm:w-[170px]" data-testid="transactions-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {allCategoriesForCombo.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={cardFilter} onValueChange={setCardFilter}>
            <SelectTrigger className="h-10 w-full sm:w-[180px]" data-testid="transactions-filter-card"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cards</SelectItem>
              {cards.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.bank} \u2014 {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Phase 18 (issue #3) \u2014 advanced filter dropdown (month + custom date range).
              Pulled into a Popover so the filter bar stays compact on the
              default view while still exposing all dimensions one click away. */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-10 gap-2 col-span-2 sm:col-span-1"
                data-testid="transactions-filter-advanced-trigger"
              >
                <FilterIcon className="h-3.5 w-3.5" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="outline" className="ml-1 text-[10px] h-4 px-1.5">{activeFilterCount}</Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3 p-4">
              <div className="space-y-1">
                <Label className="text-xs">Month</Label>
                <Select value={monthFilter} onValueChange={setMonthFilter}>
                  <SelectTrigger className="h-9" data-testid="transactions-filter-month"><SelectValue placeholder="Any month" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any month</SelectItem>
                    {monthsAvailable.map((m) => (
                      <SelectItem key={m} value={m}>
                        {new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setMonthFilter('all'); }}
                    className="h-9"
                    data-testid="transactions-filter-date-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setMonthFilter('all'); }}
                    className="h-9"
                    data-testid="transactions-filter-date-to"
                  />
                </div>
              </div>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="w-full gap-1 h-8 text-xs"
                  data-testid="transactions-filter-clear"
                >
                  <X className="h-3 w-3" /> Clear all filters
                </Button>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      <Card className="p-3">
        {filtered.length === 0 ? <div className="p-10 text-center text-sm text-muted-foreground">No transactions found.</div> :
          filtered.map((t) => (
            <TransactionRow
              key={t.id}
              tx={{ ...t, rewardValue: t.reward_value_inr, rewardCurrency: t.reward_currency, cardName: t.card_name, missed: t.missed_inr }}
              onEdit={() => openEdit(t)}
              onDeleted={(id) => setTxns((prev) => prev.filter((x) => x.id !== id))}
            />
          ))
        }
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="transaction-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit transaction</DialogTitle>
            <DialogDescription className="text-xs">
              Updating this row will recompute rewards, period caps, and milestone progress.
            </DialogDescription>
          </DialogHeader>
          <TransactionFormFields
            form={editForm}
            setForm={setEditForm}
            cards={cards}
            suggestions={allCategoriesForCombo}
            testidPrefix="transaction-edit"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={closeEdit} disabled={editSaving} data-testid="transaction-edit-cancel">Cancel</Button>
            <Button onClick={submitEdit} disabled={editSaving} className="gap-1.5" data-testid="transaction-edit-save">
              {editSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
